// HARDEN-pwa in the in-memory API: `push.config`, `push.subscribe`, `push.unsubscribe`
// (services/api/src/domain/push/subscriptions.ts), with the real routes' rules — an inbox owner
// only (a person membership; 404 otherwise), `push_disabled` while push is off, an https endpoint
// at a known push service, a 65-byte `p256dh` and a 16-byte `auth`, the same browser again a
// no-op, another person on the same browser taking it over. Off by default, as a server without
// VAPID keys; `setEnabled(true)` (the `__companyosFakePush` hook) switches it on with a fixed
// test public key. Nothing is ever pushed: the twin has no service worker.
import type { OperationInput, OperationOutput, RouteId } from "@companyos/sdk";
import { type Ctx, json, problem, validation } from "./fake";
import type { ComposedHandler } from "./fake-merge";

type View = OperationOutput<"push.subscribe">;
type Body = OperationInput<"push.subscribe">["body"];

/** A valid uncompressed P-256 point (test key; its private half is not kept anywhere). */
export const FAKE_VAPID_PUBLIC_KEY =
  "BKuIB8232iBUyvf5lywBWEedwxlUWcjmFCobqBvsT5loRHLcM7YOCn1Vbq-4tRqc5G5NMQQKKB9X2q7uEnqhvAg";
export const FAKE_PUSH_OFF_REASON = "Desktop push is not set up on this server (no VAPID keys).";

export const FAKE_PUSH_ROUTES = [
  "push.config",
  "push.subscribe",
  "push.unsubscribe",
] as const satisfies readonly RouteId[];

const SUFFIXES = [
  "fcm.googleapis.com",
  "android.googleapis.com",
  "push.services.mozilla.com",
  "push.apple.com",
  "notify.windows.com",
];

function serviceOf(endpoint: string): string | null {
  try {
    const u = new URL(endpoint);
    const h = u.hostname.toLowerCase();
    if (u.protocol !== "https:" || u.username || u.password || (u.port && u.port !== "443"))
      return null;
    return SUFFIXES.some((s) => h === s || h.endsWith(`.${s}`)) ? h : null;
  } catch {
    return null;
  }
}

const bytes = (b64url: string) => {
  try {
    return atob(b64url.replace(/-/g, "+").replace(/_/g, "/")).length;
  } catch {
    return -1;
  }
};

async function sha256Hex(text: string): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

type Row = {
  id: string;
  company: string;
  membership: string;
  endpoint: string;
  p256dh: string;
  auth: string;
  createdAt: string;
};

export function createPushFake(deps: { now: () => Date }) {
  let enabled = false;
  const rows: Row[] = [];
  let seq = 0;

  const meIn = (ctx: Ctx): string | null =>
    ctx.me.memberships.find(
      (m) => m.company_id === ctx.principal?.company.id && m.kind === "person",
    )?.membership_id ?? null;

  const viewOf = async (r: Row): Promise<View> => ({
    id: r.id,
    endpoint_sha256: await sha256Hex(r.endpoint),
    push_service: serviceOf(r.endpoint) ?? new URL(r.endpoint).hostname,
    created_at: r.createdAt,
    last_success_at: null,
  });

  const handlers: Partial<Record<RouteId, ComposedHandler>> = {
    "push.config": async (ctx) => {
      const p = ctx.principal;
      if (!p) return problem(404, "not_found", "Not found");
      const me = meIn(ctx);
      const mine = rows.filter((r) => r.company === p.company.id && r.membership === me);
      return json({
        enabled,
        public_key: enabled ? FAKE_VAPID_PUBLIC_KEY : null,
        reason: enabled ? null : FAKE_PUSH_OFF_REASON,
        subscriptions: me ? await Promise.all(mine.map(viewOf)) : [],
      });
    },
    "push.subscribe": async (ctx) => {
      const p = ctx.principal;
      const me = meIn(ctx);
      if (!p || !me) return problem(404, "not_found", "Not found");
      if (!enabled)
        return problem(409, "push_disabled", "Web push is not configured on this server");
      const body = (ctx.body ?? {}) as Partial<Body>;
      if (typeof body.endpoint !== "string" || !serviceOf(body.endpoint))
        return validation("endpoint: not an https endpoint of a known browser push service");
      if (!body.keys || bytes(body.keys.p256dh) !== 65)
        return validation("keys.p256dh: not a P-256 public key");
      if (bytes(body.keys.auth) !== 16) return validation("keys.auth: not a 16-byte auth secret");
      const { endpoint, keys } = body as Body;
      const prior = rows.find((r) => r.company === p.company.id && r.endpoint === endpoint);
      if (prior) {
        prior.membership = me;
        prior.p256dh = keys.p256dh;
        prior.auth = keys.auth;
        return json(await viewOf(prior));
      }
      const row: Row = {
        id: `0199f2a4-0062-7000-8000-${String(++seq).padStart(12, "0")}`,
        company: p.company.id,
        membership: me,
        endpoint,
        p256dh: keys.p256dh,
        auth: keys.auth,
        createdAt: deps.now().toISOString(),
      };
      rows.push(row);
      return json(await viewOf(row));
    },
    "push.unsubscribe": (ctx) => {
      const p = ctx.principal;
      if (!p || !meIn(ctx)) return problem(404, "not_found", "Not found");
      const endpoint = (ctx.body as { endpoint?: unknown } | null)?.endpoint;
      if (typeof endpoint !== "string") return validation("endpoint: required");
      for (let i = rows.length - 1; i >= 0; i--)
        if (rows[i]?.company === p.company.id && rows[i]?.endpoint === endpoint) rows.splice(i, 1);
      return new Response(null, { status: 204 });
    },
  };

  return {
    handlers,
    setEnabled(on: boolean) {
      enabled = on;
    },
  };
}
